import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatCard, MatCardContent } from '@angular/material/card';
import { catchError, debounceTime, distinctUntilChanged, map, Observable, of, startWith, switchMap, tap } from 'rxjs';
import { FormControl } from '@angular/forms';
import { Alert, AlertModule, AlertService, AlertType, ContentTableModule } from '@michelin/theme';
import { SharedModule } from '../../shared/shared.module';
import { YearScrollerComponent } from './year-scroller/year-scroller.component';
import { QuantityTableComponent } from './quantity-table/quantity-table.component';
import { AddNewFleetModalComponent } from './add-new-fleet-modal/add-new-fleet-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { NavigationConfirmService } from '../../shared/services/navigation-confirm.service';
import { AutocompleteComponent } from '../../shared/components/autocomplete/autocomplete.component';
import { HourTableComponent } from './hour-table/hour-table.component';
import { ContextService } from '../../shared/services/context.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ModificationButtonsComponent } from '../../shared/components/modification-buttons/modification-buttons.component';
import { TruckSpec as FleetTruckSpec } from '../../shared/models/fleet/truckSpec';
import { TruckSpec as TireTruckSpec } from '../../shared/models/tire/TruckSpec';
import { FleetService } from '../../shared/services/fleet.service';
import { Fleet } from '../../shared/models/fleet/fleet';
import moment from 'moment';
import {
  SaveRecalcConfirmDialogComponent
} from "../../shared/components/save-recalc-confirm-dialog/save-recalc-confirm-dialog.component";

@Component({
  selector: 'app-fleet',
  standalone: true,
  imports: [
    TranslatePipe,
    MatIcon,
    MatCard,
    MatCardContent,
    ContentTableModule,
    SharedModule,
    YearScrollerComponent,
    QuantityTableComponent,
    AlertModule,
    AutocompleteComponent,
    HourTableComponent,
    ModificationButtonsComponent
  ],
  templateUrl: 'fleet.component.html',
  styleUrls: ['./fleet.component.scss']
})
export class FleetComponent implements OnInit {
  /**
   * Models Auto-complete
   */
  modelFilterCtrl = new FormControl<string>('');
  filteredModels$!: Observable<FleetTruckSpec[]>;
  isModelsLoading: boolean = false;
  modelLoadingError: string | null = null;
  /**
   * Search parameters
   */
  nextMonthDate: Date = moment().add(1, 'M').toDate();
  nextMonth = this.nextMonthDate.getMonth() + 1;
  currentYear: number = this.nextMonthDate.getFullYear();
  truckModel: string = '';
  siteId: number;

  /**
   * Two-way bound property to know if there are modifications pending on the quantity table or hours table
   */
  isModificationsPending: boolean = false;
  isLoadingData: boolean = true;
  isSavingData: boolean = false;

  /**
   * Add New Fleet Modal
   */
  readonly dialog = inject(MatDialog);

  /**
   * Table selector
   */
  selectedTable: 'quantities' | 'hours' = 'quantities';
  @ViewChild(QuantityTableComponent) quantityTableComponent: QuantityTableComponent;
  @ViewChild(HourTableComponent) hourTableComponent: HourTableComponent;

  constructor(
    private navigationConfirm: NavigationConfirmService,
    private cdr: ChangeDetectorRef,
    private context: ContextService,
    private destroyRef: DestroyRef,
    private alertService: AlertService,
    private translate: TranslateService,
    private fleetService: FleetService
  ) {}

  private setupAutoComplete() {
    this.filteredModels$ = this.modelFilterCtrl.valueChanges.pipe(
      startWith(this.truckModel ?? ''),
      tap(() => {
        this.isModelsLoading = true;
      }),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((input: string | FleetTruckSpec | null) => {
        let search = '';
        if (input && typeof input === 'string') {
          search = input.trim().toLowerCase();
          this.truckModel = input;
        } else if (input && typeof input === 'object') {
          const raw = (input as FleetTruckSpec).specification;
          search = String(raw).trim().toLowerCase();
          this.truckModel = String(raw);
        } else {
          search = '';
          this.truckModel = '';
        }

        const specs = this.getAvailableTruckSpecs();
        const results = specs.filter((spec) =>
          (spec.specification ?? '').toLowerCase().includes(search)
        );
        return of(results);
      }),
      map((items) => {
        this.isModelsLoading = false;
        this.modelLoadingError = null;
        return items || ([] as FleetTruckSpec[]);
      }),
      catchError(() => {
        this.isModelsLoading = false;
        this.modelLoadingError = 'Failed to fetch results. Please try again.';
        return of([] as FleetTruckSpec[]);
      })
    ) as Observable<FleetTruckSpec[]>;
  }

  private getAvailableTruckSpecs(): FleetTruckSpec[] {
    const active = this.selectedTable === 'quantities' ? this.quantityTableComponent : this.hourTableComponent;

    if (active && active.dataSource && Array.isArray(active.dataSource.data) && active.dataSource.data.length) {
      return active.dataSource.data.map((d) => d.truckSpec as FleetTruckSpec);
    }

    return [];
  }
  openAddNewFleetModal(): void {
    const existingTruckSpecIds: number[] = [];
    if (this.quantityTableComponent) {
      existingTruckSpecIds.push(...this.quantityTableComponent.getExistingTruckSpecIds());
    } else {
      existingTruckSpecIds.push(...this.hourTableComponent.getExistingTruckSpecIds());
    }

    const dialogRef = this.dialog.open(AddNewFleetModalComponent, {
      data: {
        siteId: this.siteId,
        existingTruckSpecIds
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.selection) {
        this.addNewFleets(result.selection as TireTruckSpec[]);
      }
    });
  }

  protected addNewFleets(selection: TireTruckSpec[]) {
    this.fleetService
      .generateFleets(this.siteId, this.currentYear, this.nextMonth, selection)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: Fleet[]) => {
          if (this.quantityTableComponent) {
            this.quantityTableComponent.addNewFleets(result);
          } else if (this.hourTableComponent) {
            this.hourTableComponent.addNewFleets(result);
          }
        },
        error: (err: any) => {
          let errorMsg: string | undefined;
          if (err?.error?.message) {
            errorMsg = `(${err.error.message})`;
          }
          let selectionDisplay: string = selection[0]?.model;
          for (let i = 1; i < selection.length; i++) {
            selectionDisplay = selectionDisplay + '/' + selection[i].model;
          }
          this.alertService.showAlert({
            type: AlertType.critical,
            title: `${this.translate.instant('fleet.card.table.add.errorTitle')} ${selectionDisplay}`,
            content: `${this.translate.instant('fleet.card.table.add.errorMsg')} ${errorMsg}`
          } as Alert);
        }
      });
  }

  protected saveModifications() {
    if (this.quantityTableComponent) {
      this.quantityTableComponent.saveModifications(false);
    } else if (this.selectedTable === 'hours' && this.hourTableComponent) {
      this.hourTableComponent.saveModifications(false);
    }
  }

  protected onConfirmSaveAndRecalculate() {
    const dialogRef = this.dialog.open(SaveRecalcConfirmDialogComponent);

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.saveAndRecalculate();
      }
    });
  }

  protected saveAndRecalculate() {
    if (this.quantityTableComponent) {
      this.quantityTableComponent.saveModifications(true);
    } else if (this.selectedTable === 'hours' && this.hourTableComponent) {
      this.hourTableComponent.saveModifications(true);
    }
  }

  protected resetModifications() {
    if (this.quantityTableComponent) {
      this.quantityTableComponent.resetModifications();
    } else if (this.selectedTable === 'hours' && this.hourTableComponent) {
      this.hourTableComponent.resetModifications();
    }
  }

  protected switchTable(table: 'quantities' | 'hours') {
    if (this.isModificationsPending) {
      this.navigationConfirm.confirmNavigation(() => {
        this.selectedTable = table;
      });
    } else {
      this.selectedTable = table;
    }
  }

  protected onElementSelected(truckModel: FleetTruckSpec) {
    this.truckModel = truckModel.model;
  }

  protected onSearchChanged(truckModel: string) {
    this.truckModel = truckModel;
  }

  protected onTableDataLoaded() {
    this.setupAutoComplete();
    this.cdr.detectChanges();
  }

  protected getQtyActiveClass() {
    if (this.selectedTable === 'quantities') {
      return 'active-button';
    }
  }

  protected getHoursActiveClass() {
    if (this.selectedTable === 'hours') {
      return 'active-button ';
    }
  }

  ngOnInit(): void {
    this.context.siteId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
      if (id !== null && id !== this.siteId) {
        this.siteId = id;
      }
    });
  }
}
